import React, { useEffect, useRef, useState } from 'react';

import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import SelectDialog, {
  SelectDialogSubmitButton,
  SelectDialogTable
} from '../../../common/dialogs/SelectDialog';
import { useFindRoles } from '../../roles/Role.hooks';
import { Role } from '../../roles/Role.interface';
import { useAddRolesToGroup } from '../Group.hooks';
import AppSnackbar from '../../../common/AppSnackbar';
import { roles as roleLabels } from '../../../user/User.interface';
import { ConfirmDialog } from '@sierralabs/react-material-ui';

export interface GroupRoleAssignDialogProps {
  groupId: number;
  roles: Role[];
  onCancel?: () => void;
  onSubmit?: (updatedRoles: Role[]) => void;
}

const GroupRoleAssignDialog: React.FC<GroupRoleAssignDialogProps> = ({
  groupId,
  onCancel,
  onSubmit,
  roles
}) => {
  // const [search, setSearch] = useState('');
  const [roleChanges, setRoleChanges] = useState<Role[]>();
  const onSubmitRef = useRef(onSubmit);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const { roleList, isLoading, error, get } = useFindRoles();
  useEffect(() => get({ page, limit }), [get, limit, page]);

  const {
    updatedGroupRoles,
    updatingGroupRoles,
    errorUpdatingGroupRoles,
    addRolesToGroup
  } = useAddRolesToGroup(groupId);
  useEffect(() => {
    if (updatedGroupRoles) {
      onSubmitRef.current?.(updatedGroupRoles);
    }
  }, [updatedGroupRoles]);

  return (
    <SelectDialog open onClose={onCancel} selectedItems={roles}>
      <DialogTitle>Assign Roles to Group</DialogTitle>
      <DialogContent>
        <DialogContentText>Assign roles below to the group</DialogContentText>
        {/* <SelectDialogSearchField
          label='Role'
          onSearch={(value: string) => {
            setPage(0);
            setSearch(value);
          }}
        /> */}
        <SelectDialogTable
          multiple
          idKey='id'
          columns={[
            {
              key: 'name',
              label: 'Role',
              render: role =>
                roleLabels.find(r => r.name === role.name)?.label || role.name
            }
          ]}
          isLoading={isLoading}
          dataList={roleList}
          page={page}
          rowsPerPage={limit}
          onChangePage={page => setPage(page)}
          onChangeRowsPerPage={limit => setLimit(limit)}
        />
        {error && (
          <Alert severity='error'>
            Error getting data. Please check your internet connection and try
            again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <SelectDialogSubmitButton
          disabled={updatingGroupRoles}
          isSubmitting={updatingGroupRoles}
          onSubmit={roles => {
            console.log('roles', roles);
            setRoleChanges(roles);
          }}
        >
          Assign
        </SelectDialogSubmitButton>
      </DialogActions>
      {roleChanges && (
        <ConfirmDialog
          open
          title='Update User Roles'
          message='Would you like to update all user roles with these new settings?'
          cancelLabel='No'
          confirmLabel='Yes'
          onCancel={() => {
            if (roleChanges) {
              addRolesToGroup(
                roleChanges.map(r => r.id),
                false
              );
            }
            setRoleChanges(undefined);
          }}
          onConfirm={() => {
            if (roleChanges) {
              addRolesToGroup(
                roleChanges.map(r => r.id),
                true
              );
            }
            setRoleChanges(undefined);
          }}
        />
      )}
      {errorUpdatingGroupRoles && (
        <AppSnackbar severity='error' open>
          Error assigning roles.
        </AppSnackbar>
      )}
    </SelectDialog>
  );
};

export default GroupRoleAssignDialog;
